import React, { useEffect, useRef, useState } from 'react';
import { CartItem } from '../types';
import { gsap } from 'gsap';

const getFullImageUrl = (url: string | null | undefined): string => {
  if (!url) return 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=600&q=80';
  if (url.startsWith('http')) return url;
  const apiBase = (import.meta.env.VITE_API_BASE_URL || 'http://localhost:7202/api').replace('/api', '');
  return `${apiBase}${url}`;
};

interface CartProps {
  cart: CartItem[];
  onUpdateQuantity: (id: string, delta: number) => void;
  onRemove: (id: string) => void;
  onGoToCheckout: (opts: { includeDelivery: boolean; includeServiceFee: boolean; deliveryFee: number; serviceFee: number; total: number }) => void;
  onBackToMenu: () => void;
  onTrackOrder?: () => void;
  cartSummary?: {
    subtotal: number;
    deliveryFee: number;
    serviceFee: number;
    total: number;
  };
}

const Cart: React.FC<CartProps> = ({
  cart,
  onUpdateQuantity,
  onRemove,
  onGoToCheckout,
  onBackToMenu,
  onTrackOrder,
  cartSummary = {
    subtotal: cart.reduce((acc, item) => acc + (item.price * item.quantity), 0),
    deliveryFee: 40.0,
    serviceFee: 15.0,
    total: 0
  }
}) => {
  const { subtotal, deliveryFee, serviceFee } = cartSummary;
  const containerRef = useRef<HTMLDivElement>(null);
  const [includeDelivery, setIncludeDelivery] = useState(false);
  const [includeServiceFee, setIncludeServiceFee] = useState(false);
  const [editableDeliveryFee, setEditableDeliveryFee] = useState(deliveryFee);
  const [editableServiceFee, setEditableServiceFee] = useState(serviceFee);

  const actualDeliveryFee = includeDelivery ? editableDeliveryFee : 0;
  const actualServiceFee = includeServiceFee ? editableServiceFee : 0;
  const total = subtotal + actualDeliveryFee + actualServiceFee;

  useEffect(() => {
    if (cart.length > 0) {
      const ctx = gsap.context(() => {
        gsap.fromTo(".cart-item-card",
          { opacity: 0, x: -20 },
          { opacity: 1, x: 0, duration: 0.5, stagger: 0.1, ease: 'power2.out' }
        );
        gsap.fromTo(".summary-card",
          { opacity: 0, scale: 0.95 },
          { opacity: 1, scale: 1, duration: 0.6, delay: 0.3, ease: 'back.out(1.7)' }
        );
      }, containerRef);
      return () => ctx.revert();
    }
  }, [cart.length]);

  useEffect(() => {
    setEditableDeliveryFee(deliveryFee);
  }, [deliveryFee]);

  useEffect(() => {
    setEditableServiceFee(serviceFee);
  }, [serviceFee]);

  if (cart.length === 0) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-32 h-32 rounded-full bg-gray-100 dark:bg-white/5 flex items-center justify-center mb-8 animate-bounce">
          <span className="material-symbols-outlined text-6xl text-gray-300 dark:text-gray-600">shopping_cart_off</span>
        </div>
        <h2 className="text-3xl font-black text-secondary dark:text-white mb-3">Tu carrito está vacío</h2>
        <p className="text-gray-500 dark:text-gray-400 font-medium mb-10 max-w-xs">Parece que aún no has añadido nada delicioso a tu pedido.</p>
        <button
          onClick={onBackToMenu}
          className="relative z-[60] pointer-events-auto inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-gray-500/50 dark:bg-white/5 backdrop-blur-md text-white dark:text-white font-black text-xs uppercase tracking-widest border border-gray-100 dark:border-white/10 hover:bg-gray-400 hover:dark:bg-white/10 transition-all active:scale-95 mb-4"
        >
          <span className="material-symbols-outlined text-sm">arrow_back</span>
          Volver
        </button>
        <button
          onClick={onBackToMenu}
          className="btn-primary-premium px-10"
        >
          Explorar Restaurantes
        </button>
      </div>
    );
  }

  const vendors = Array.from(new Set(cart.map(item => item.vendorName)));

  return (
    <div ref={containerRef} className="max-w-[1200px] mx-auto pt-14 pb-32 px-2 lg:px-2">
      <div className="mb-2">
        <button
          onClick={onBackToMenu}
          className="relative z-[60] pointer-events-auto inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-gray-500/50 dark:bg-white/5 backdrop-blur-md text-white dark:text-white font-black text-xs uppercase tracking-widest border border-gray-100 dark:border-white/10 hover:bg-gray-400 hover:dark:bg-white/10 transition-all active:scale-95 mt-4"
        >
          <span className="material-symbols-outlined text-sm">arrow_back</span>
          Volver
        </button>
      </div>
      <div className="flex flex-col mb-2">
        <h1 className="text-4xl md:text-5xl font-black text-secondary dark:text-white tracking-tight mb-2">Mi Carrito</h1>
        <div className="flex items-center gap-3 text-sm font-bold text-gray-400">
          <span className="px-3 py-1 bg-gray-100 dark:bg-white/5 rounded-full">{vendors.length} Negocios</span>
          <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
          <span className="px-3 py-1 bg-gray-100 dark:bg-white/5 rounded-full">{cart.length} Artículos</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start shadow-premium-hover bg-gray-200 dark:bg-slate-800/50 rounded-[2.5rem] p-2">
        <div className="lg:col-span-8 space-y-8 dark:bg-slate-800/50 dark:rounded-[2.5rem]">
          {vendors.map((vendor) => (
            <div key={vendor} className="cart-item-card overflow-hidden">
              <div className="flex items-center gap-4 mb-5 ml-2">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined font-black">store</span>
                </div>
                <h3 className="text-xl font-black text-secondary dark:text-white">{vendor}</h3>
              </div>

              <div className="glass-card rounded-[2.5rem] overflow-hidden">
                <div className="divide-y divide-gray-100 dark:divide-white/5">
                  {cart.filter(item => item.vendorName === vendor).map((item) => (
                    <div key={item.id} className="p-6 md:p-8 flex flex-col sm:flex-row gap-6 hover:bg-white/40 dark:hover:bg-white/[0.02] transition-colors group">
                      <div className="relative w-full sm:w-32 h-32 rounded-3xl overflow-hidden shrink-0">
                        <img className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" src={getFullImageUrl(item.image)} alt={item.name} />
                      </div>

                      <div className="flex-1 flex flex-col justify-between py-1">
                        <div>
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="text-lg font-black text-secondary dark:text-white leading-tight">{item.name}</h4>
                            <span className="text-lg font-black text-primary sm:hidden">${item.price.toFixed(0)}</span>
                          </div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 line-clamp-2">{item.description}</p>
                        </div>

                        <div className="flex items-center justify-between mt-6 sm:mt-0">
                          <span className="hidden sm:block text-xl font-black text-primary">${item.price.toFixed(0)}</span>

                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-1 bg-background-light dark:bg-black/20 p-1 rounded-2xl border border-gray-100 dark:border-white/5 shadow-inner">
                              <button
                                onClick={() => onUpdateQuantity(item.id, -1)}
                                className="w-10 h-10 flex items-center justify-center rounded-xl bg-white dark:bg-white/5 text-secondary dark:text-white hover:text-primary shadow-sm active:scale-95 transition-all disabled:opacity-30"
                                disabled={item.quantity <= 1}
                              >
                                <span className="material-symbols-outlined text-sm font-bold">remove</span>
                              </button>
                              <span className="w-10 text-center font-black text-secondary dark:text-white">{item.quantity}</span>
                              <button
                                onClick={() => onUpdateQuantity(item.id, 1)}
                                className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary text-white shadow-lg shadow-primary/20 active:scale-95 transition-all"
                              >
                                <span className="material-symbols-outlined text-sm font-bold">add</span>
                              </button>
                            </div>

                            <button
                              onClick={() => onRemove(item.id)}
                              className="w-12 h-12 flex items-center justify-center rounded-2xl text-gray-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all hover:rotate-6 active:scale-90"
                            >
                              <span className="material-symbols-outlined">delete</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-4 sticky top-24 dark:bg-slate-800/50 dark:rounded-[2.5rem] bg-white rounded-[2.5rem]">
          <div className="summary-card glass-card rounded-[2.5rem] p-8 md:p-10 shadow-premium-hover shadow-premium">
            <h3 className="text-2xl font-black text-secondary dark:text-white mb-8">Resumen</h3>

            <div className="space-y-4 mb-8">
              <div className="flex justify-between text-sm font-bold text-gray-500 dark:text-gray-400">
                <span>Subtotal</span>
                <span className="text-secondary dark:text-white">${subtotal.toFixed(0)}</span>
              </div>
              <div className="flex justify-between items-center text-sm font-bold text-gray-500 dark:text-gray-400">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <button
                    type="button"
                    onClick={() => setIncludeDelivery(!includeDelivery)}
                    className={`relative w-10 h-6 rounded-full transition-colors duration-200 ${
                      includeDelivery ? 'bg-primary' : 'bg-gray-300 dark:bg-white/10'
                    }`}
                  >
                    <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                      includeDelivery ? 'translate-x-4' : 'translate-x-0'
                    }`} />
                  </button>
                  <span>Envío</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className={`text-secondary dark:text-white transition-opacity ${!includeDelivery ? 'opacity-40' : ''}`}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={editableDeliveryFee}
                    onChange={(event) => setEditableDeliveryFee(Math.max(0, Number(event.target.value) || 0))}
                    className={`w-20 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800/50 px-3 py-1 text-right text-secondary dark:text-white outline-none transition-opacity ${!includeDelivery ? 'opacity-40 line-through' : ''}`}
                  />
                </div>
              </div>
              <div className="flex justify-between items-center text-sm font-bold text-gray-500 dark:text-gray-400">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <button
                    type="button"
                    onClick={() => setIncludeServiceFee(!includeServiceFee)}
                    className={`relative w-10 h-6 rounded-full transition-colors duration-200 ${
                      includeServiceFee ? 'bg-primary' : 'bg-gray-300 dark:bg-white/10'
                    }`}
                  >
                    <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                      includeServiceFee ? 'translate-x-4' : 'translate-x-0'
                    }`} />
                  </button>
                  <span>Tarifa de servicio</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className={`text-secondary dark:text-white transition-opacity ${!includeServiceFee ? 'opacity-40' : ''}`}>$</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={editableServiceFee}
                    onChange={(event) => setEditableServiceFee(Math.max(0, Number(event.target.value) || 0))}
                    className={`w-20 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-800/50 px-3 py-1 text-right text-secondary dark:text-white outline-none transition-opacity ${!includeServiceFee ? 'opacity-40 line-through' : ''}`}
                  />
                </div>
              </div>
              <div className="p-6 mt-6 border-t border-gray-100 dark:border-white/5 flex justify-between items-end dark:bg-slate-800/50 dark:rounded-[2.5rem]">
                <span className="text-lg font-black text-secondary dark:text-white ">Total</span>
                <span className="text-3xl font-black text-primary">${total.toFixed(0)}</span>
              </div>
            </div>

            <button
              onClick={() => onGoToCheckout({ includeDelivery, includeServiceFee, deliveryFee: editableDeliveryFee, serviceFee: editableServiceFee, total })}
              className="btn-primary-premium w-full group py-5 bg-green-500 rounded-[2.5rem] text-white hover:bg-green-600 transition-colors"
            >
              <span>Continuar al Pago</span>
              <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">arrow_forward</span>
            </button>

            <p className="mt-8 flex items-center justify-center gap-2 text-[11px] font-black text-green-500 uppercase tracking-widest">
              <span className="material-symbols-outlined text-[16px] filled">verified</span>
              Pago 100% Seguro
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
